import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AppSessionService } from "../app-session/app-session.service";

import {
  setAdvancedFilterQuery,
  setSimpleFilterQuery,
} from "../../utils/tableUtils";
import { MultimediaFileDTO } from "src/app/models/playlists/multimediaFileDTO";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class ManageFileService {
  private FILES_URL = this.appSession.baseUrl + "/api/v1/multimedia/files";

  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}

  createFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    const url = `${this.FILES_URL}`;
    return this.http.post<any>(url, formData).pipe(
      catchError((error) => throwError(error))
    );
  }

  fileDetails(fileId: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.FILES_URL}/${fileId}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  deleteFile(id: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.FILES_URL}/${id}`;
    return this.http.delete<any>(url, { headers }).pipe(
      catchError((error) => throwError(error))
    );
  }

  getFilesList(filteringArgs?: any): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (filterQuery.length > 0) {
      headers = headers.append("query", `${filterQuery}`);
    }

    const url = `${this.FILES_URL}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => ({
        ...result,
        items: result.items.map((file: MultimediaFileDTO) => ({
          ...file,
          id: (file as any).id ?? (file as any).token ?? (file as any).fileId
        })),
      })),
      catchError((error) => throwError(error))
    );
  }

  private setHeaders(
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-type': 'application/json' });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }
    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }
    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery = `${active},${String(direction || '').toUpperCase()}`;
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        'query',
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0 ? FilterOperation.AND : ''
        } ${advancedFilterQuery}`.trim()
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append('sort', orderQuery);
    }

    return headers;
  }

  private buildDataUrl(
    pagingArgs: any,
    _headers: HttpHeaders,
    path: string
  ): string {
    let base = `${this.FILES_URL}${path}`; // e.g. /api/v1/multimedia/files/search?
    const scrollingQuery = this._buildScrollExpression(pagingArgs);
    if (scrollingQuery) {
      base += scrollingQuery; // result: ?&real_size=true&offset=0&limit=10
    }
    return base;
  }

  private _buildScrollExpression(pagingArgs: any): string {
    if (!pagingArgs) return EMPTY_STRING;
    const skip = Number(pagingArgs?.skip ?? 0);
    const top = pagingArgs?.top;
    const real = pagingArgs?.realSize === true;

    const parts: string[] = [`real_size=${real}`, `offset=${skip}`];
    if (top != null) parts.push(`limit=${top}`);

    // contracts-style: ?...&real_size=true&offset=0&limit=10
    return `&${parts.join("&")}`;
  }

  getFilesList2(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    // filters/sort stay in headers
    const headers = this.setHeaders(filteringArgs, sortingArgs, advancedFilteringArgs);

    // paging goes in URL
    const url = this.buildDataUrl(paging, headers, "?"); // base /files + ? + real_size/offset/limit

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => ({
        ...result,
        items: (result?.items ?? []).map((file: MultimediaFileDTO) => ({
          ...file,
          id: (file as any).id ?? (file as any).token ?? (file as any).fileId
        })),
      })),
      catchError((error) => throwError(error))
    );
  }

  private appendPagingHeaders(pagingArgs: any, headers: HttpHeaders): HttpHeaders {
    if (!pagingArgs) return headers;
    const offset = pagingArgs.skip ?? 0;
    const limit = pagingArgs.top;
    const realSize = !!pagingArgs.realSize;

    headers = headers.append("offset", String(offset));
    if (limit != null) headers = headers.append("limit", String(limit));
    headers = headers.append("real_size", String(realSize));
    return headers;
  }

  getFileThumbnail(fileId: string, isThumbnail = true): Observable<Blob> {
    const url = `${this.FILES_URL}/${fileId}/download?isThumbnail=${isThumbnail}`; 
    return this.http.get(url, { responseType: 'blob' });
  }
}
